import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Tooltip,
} from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ContentCopy as DuplicateIcon,
  Visibility as PreviewIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { tracksAPI, domainsAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { useTrackTypes, getTypeDefaults } from '../hooks/useTrackTypes'
import PathwayIcon from '../components/presentation/PathwayIcon'

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'draft', label: 'Draft' },
  { value: 'archived', label: 'Archived' },
]

// Status indicator styles
const statusStyles = {
  active: { color: '#16a34a', label: 'Active' },
  draft: { color: '#71717a', label: 'Draft' },
  archived: { color: '#dc2626', label: 'Archived' },
}

function StatusIndicator({ status }) {
  const style = statusStyles[status] || statusStyles.draft
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: style.color,
        }}
      />
      <Typography variant="body2" sx={{ color: style.color, fontSize: '0.8125rem' }}>
        {style.label}
      </Typography>
    </Box>
  )
}

function Tracks() {
  const navigate = useNavigate()
  const { canManageBaseContent } = useAuth()
  const { types: trackTypes, typesByCode } = useTrackTypes()
  const [tracks, setTracks] = useState([])
  const [domains, setDomains] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Filter state
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState('')
  const [filterDomain, setFilterDomain] = useState('')
  const [filterStatus, setFilterStatus] = useState('')

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)
      const [tracksResponse, domainsResponse] = await Promise.all([
        tracksAPI.getAll(),
        domainsAPI.getAll(),
      ])
      setTracks(tracksResponse.data || [])
      setDomains(domainsResponse.data || [])
    } catch (err) {
      console.error('Error fetching data:', err)
      setError('Failed to load pathways. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this pathway?')) {
      return
    }
    try {
      setError(null)
      await tracksAPI.delete(id)
      fetchData()
    } catch (err) {
      console.error('Error deleting pathway:', err)
      setError(err.response?.data?.message || 'Failed to delete pathway.')
    }
  }

  const handleDuplicate = async (track) => {
    try {
      setError(null)
      await tracksAPI.duplicate(track.id, {
        code: `${track.code}_copy`,
        name: `${track.name} (Copy)`,
      })
      fetchData()
    } catch (err) {
      console.error('Error duplicating pathway:', err)
      setError(err.response?.data?.message || 'Failed to duplicate pathway.')
    }
  }

  const canEditTrack = (track) => {
    if (track.is_base && !canManageBaseContent('tracks')) return false
    return true
  }

  const canDeleteTrack = (track) => {
    if (track.is_base && !canManageBaseContent('tracks')) return false
    return true
  }

  // Filter tracks based on current filters
  const filteredTracks = tracks.filter((track) => {
    const matchesSearch =
      !searchTerm ||
      track.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      track.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      track.description?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesType = !filterType || track.track_type === filterType
    const matchesDomain = !filterDomain || track.domain === filterDomain
    const matchesStatus = !filterStatus || (track.is_active ? 'active' : 'archived') === filterStatus
    return matchesSearch && matchesType && matchesDomain && matchesStatus
  })

  const getTrackStatus = (track) => {
    if (!track.is_active) return 'archived'
    const isComplete = track.metadata?.agent_instructions
    return isComplete ? 'active' : 'draft'
  }

  const columns = [
    {
      field: 'name',
      headerName: 'Pathway',
      flex: 1,
      minWidth: 220,
      renderCell: (params) => {
        // Resolved app presentation: library value, then the type default
        const presentation = params.row.presentation || {}
        const defaults = getTypeDefaults(typesByCode[params.row.track_type])
        const icon = presentation.icon || defaults.icon
        return (
          <Box display="flex" alignItems="center" gap={1.25} minWidth={0}>
            <PathwayIcon name={icon} size={18} sx={{ color: 'text.secondary' }} />
            <Box minWidth={0}>
              <Typography variant="body2" fontWeight={500} noWrap>
                {params.row.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" fontFamily="monospace" fontSize="0.6875rem" noWrap display="block">
                {params.row.code}
                {presentation.display_name && (
                  <Box component="span" sx={{ fontFamily: 'inherit', ml: 1 }}>
                    · App: “{presentation.display_name}”
                  </Box>
                )}
              </Typography>
            </Box>
          </Box>
        )
      },
    },
    {
      field: 'is_base',
      headerName: 'Source',
      width: 70,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
          {params.value ? 'Base' : 'Custom'}
        </Typography>
      ),
    },
    {
      field: 'track_type',
      headerName: 'Type',
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2" fontSize="0.8125rem">
          {typesByCode[params.value]?.label || params.value}
        </Typography>
      ),
    },
    {
      field: 'domain',
      headerName: 'Domain',
      width: 140,
      renderCell: (params) => {
        const domain = domains.find((d) => d.code === params.value)
        return (
          <Typography variant="body2" color="text.secondary" fontSize="0.8125rem">
            {domain?.name || params.value || '—'}
          </Typography>
        )
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 90,
      renderCell: (params) => <StatusIndicator status={getTrackStatus(params.row)} />,
    },
    {
      field: 'actions',
      headerName: '',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" gap={0.5}>
          <Tooltip title={canEditTrack(params.row) ? 'Edit' : 'View'}>
            <IconButton
              size="small"
              onClick={() => navigate(`/tracks/${params.row.id}/edit`)}
            >
              {canEditTrack(params.row) ? (
                <EditIcon fontSize="small" />
              ) : (
                <PreviewIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Duplicate">
            <IconButton size="small" onClick={() => handleDuplicate(params.row)}>
              <DuplicateIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canDeleteTrack(params.row) && (
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => handleDelete(params.row.id)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ]

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h5">Pathway Library</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage intervention pathways that deliver clinical content to patients.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/tracks/new')}
        >
          Create Pathway
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <TextField
            placeholder="Search pathways..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ width: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={filterType}
              label="Type"
              onChange={(e) => setFilterType(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {trackTypes.map((type) => (
                <MenuItem key={type.code} value={type.code}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Domain</InputLabel>
            <Select
              value={filterDomain}
              label="Domain"
              onChange={(e) => setFilterDomain(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {domains.map((domain) => (
                <MenuItem key={domain.code} value={domain.code}>
                  {domain.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filterStatus}
              label="Status"
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {STATUS_OPTIONS.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {(searchTerm || filterType || filterDomain || filterStatus) && (
            <Button
              size="small"
              onClick={() => {
                setSearchTerm('')
                setFilterType('')
                setFilterDomain('')
                setFilterStatus('')
              }}
            >
              Clear
            </Button>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            {filteredTracks.length} of {tracks.length} pathways
          </Typography>
        </Box>
      </Paper>

      {/* Track List */}
      <Paper>
        <DataGrid
          rows={filteredTracks}
          columns={columns}
          pageSize={15}
          rowsPerPageOptions={[15, 25, 50]}
          autoHeight
          disableSelectionOnClick
          getRowId={(row) => row.id}
          onRowDoubleClick={(params) => navigate(`/tracks/${params.row.id}/edit`)}
        />
      </Paper>
    </Box>
  )
}

export default Tracks
